/**
 * SlotGame — Plan 08-03 Task 2 (GAME-03).
 *
 * 3-릴 슬롯 + 잭팟 확률 공시 + seeded RNG 결과 결정.
 *
 * 결정론:
 *   - 잭팟 판정 RNG = createSlotJackpotRng(eventId) → r < jackpotProbability 이면 잭팟.
 *   - 미당첨 시 릴별 독립 RNG = createSlotReelRng(eventId, i) → weighted symbol pick.
 *   - 미당첨 시 3 릴 모두 jackpotSymbol 로 일치하면 last reel 을 다음 candidate 로 교체
 *     (공시된 jackpotProbability 와 실제 빈도 일치 보장).
 *
 * 확률 공시:
 *   - meta.probabilityDisclosure = "이 슬롯의 잭팟 확률은 0.500% 입니다. 심볼 가중치: ..."
 *   - renderDisclosure 로 stage 하단에 Text 배치.
 *
 * 10K 시뮬레이션 시 잭팟 빈도는 jackpotProbability × 10K ± 10% 범위.
 */
import type { Container } from 'pixi.js';
import { Container as PixiContainer, Graphics, Text, TextStyle } from 'pixi.js';
import type {
  DonationEvent,
  Game,
  GameContext,
  GameMeta,
} from '../game-interface.js';
import type { SlotConfig } from '@loop/contracts/rest';
import { renderDisclosure } from '../probability-disclosure.js';
import { createSlotJackpotRng, createSlotReelRng } from './slot.rng.js';
import { pickWeighted } from '../seeded-rng.js';

function buildSlotDisclosure(config: SlotConfig): string {
  const pct = (config.jackpotProbability * 100).toFixed(3);
  const weights = config.symbols
    .map((s) => `${s.label}(${s.weight})`)
    .join(', ');
  return `이 슬롯의 잭팟 확률은 ${pct}% 입니다. 심볼 가중치: ${weights}`;
}

export class SlotGame implements Game {
  readonly meta: GameMeta;
  private reelsContainer: Container | null = null;
  private ctx: GameContext | null = null;

  constructor(private readonly config: SlotConfig) {
    this.meta = {
      kind: 'slot',
      name: '슬롯',
      version: '1.0.0',
      prizes: config.symbols,
      probabilityDisclosure: buildSlotDisclosure(config),
    };
    // jackpotSymbol 이 symbols 안에 존재하는지 검증 (Rule 2 correctness).
    if (!config.symbols.some((s) => s.id === config.jackpotSymbol)) {
      throw new Error(
        `jackpotSymbol '${config.jackpotSymbol}' not in symbols[]`,
      );
    }
  }

  async init(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    const reels = new PixiContainer();
    this.reelsContainer = reels;
    ctx.stage.addChild(reels);

    const reelWidth = 140;
    const reelHeight = 180;
    const gap = 20;
    const startX = 200;
    const y = 210;
    const labelStyle = new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 48,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
    });
    for (let i = 0; i < 3; i += 1) {
      const g = new Graphics();
      g.roundRect(
        startX + i * (reelWidth + gap),
        y,
        reelWidth,
        reelHeight,
        12,
      ).fill({ color: 0x1f2937 });
      reels.addChild(g);
      const t = new Text({
        text: this.config.symbols[0]?.label ?? '?',
        style: labelStyle,
      });
      t.anchor.set(0.5);
      t.x = startX + i * (reelWidth + gap) + reelWidth / 2;
      t.y = y + reelHeight / 2;
      reels.addChild(t);
    }

    renderDisclosure(ctx.stage, this.meta.probabilityDisclosure, {
      reducedMotion: ctx.reducedMotion,
      position: 'bottom',
    });
  }

  /**
   * 한 번의 스핀 → 3 symbol id 배열 반환. 공란 시뮬레이션 목적으로 public.
   * 잭팟 RNG 판정 → 당첨이면 3 릴 모두 jackpotSymbol. 미당첨이면 릴별 weighted pick,
   * 만약 3 릴이 우연히 전부 jackpotSymbol 로 일치하면 last reel 교체 (공시 확률 보장).
   */
  spin(donationEventId: string): readonly string[] {
    const jrng = createSlotJackpotRng(donationEventId);
    const jackpot = jrng.next() < this.config.jackpotProbability;
    if (jackpot) {
      return [
        this.config.jackpotSymbol,
        this.config.jackpotSymbol,
        this.config.jackpotSymbol,
      ];
    }
    const weights = this.config.symbols.map((s) => s.weight);
    const result: string[] = [];
    for (let i = 0; i < 3; i += 1) {
      const r = createSlotReelRng(donationEventId, i).next();
      const idx = pickWeighted(weights, r);
      const sym = this.config.symbols[idx];
      if (sym) result.push(sym.id);
    }
    // 공시된 jackpotProbability 와 실제 빈도 일치 보장:
    // 미당첨 경로에서 3 릴 모두 jackpotSymbol 이 되지 않도록 강제.
    if (result.every((id) => id === this.config.jackpotSymbol)) {
      // 다음 candidate 로 last reel 교체.
      const nonJp = this.config.symbols.find(
        (s) => s.id !== this.config.jackpotSymbol,
      );
      if (nonJp) {
        result[2] = nonJp.id;
      }
    }
    return result;
  }

  async onDonation(event: DonationEvent): Promise<void> {
    if (!this.ctx) return;
    const symbols = this.spin(event.eventId);
    const jackpot =
      symbols[0] === this.config.jackpotSymbol &&
      symbols[1] === this.config.jackpotSymbol &&
      symbols[2] === this.config.jackpotSymbol;

    // reducedMotion=false 는 릴 회전 애니메이션 (본 구현에서는 최종 심볼 즉시 반영;
    // 회전 연출은 추후 E2E 에서 확인 — DESIGN §10.4 준수).

    this.ctx.reportResult({
      kind: 'slot',
      winner: { symbols, jackpot },
      donationEventId: event.eventId,
      at: new Date().toISOString(),
    });
  }

  destroy(): void {
    if (this.reelsContainer) {
      this.reelsContainer.removeFromParent();
      this.reelsContainer.destroy({ children: true });
      this.reelsContainer = null;
    }
    this.ctx = null;
  }
}
